/**
 * Loan programs. `slug` doubles as the anchor id on /loan-programs/.
 * `home: true` marks the programs shown on the home page grid.
 */
const programs = [
  {
    slug: "purchase",
    name: "Home Purchase",
    short: "Conventional, FHA, VA and USDA, priced across dozens of lenders.",
    lede: "One application. Every major lender. The best fit for your file, not the one bank's rate sheet.",
    body: [
      "Buying a home is the loan most people get once or twice in a lifetime, and the difference between a good lender and a great one shows up in the rate, the fees and how calm the last two weeks feel. As an independent broker, Justin submits your file to the wholesale lender whose guidelines and pricing fit it best, which routinely means a lower rate than the same borrower would get walking into a bank.",
      "Every purchase starts with a real pre-approval: credit pulled, income and assets reviewed, and a number you can write an offer on with confidence. Your agent gets a direct line to Justin, and listing agents get a call the day your offer goes in.",
    ],
    bullets: ["Conventional, FHA, VA and USDA", "Down payments from 0% to 20%+", "Pre-approval in as little as 24 hours", "Fully underwritten approvals for competitive offers"],
    home: true,
  },
  {
    slug: "refinance",
    name: "Refinance",
    short: "Lower the rate, shorten the term, or turn equity into cash.",
    lede: "A refinance only makes sense when the math works. Justin will show you the math before you commit to anything.",
    body: [
      "Rate-and-term refinances lower your payment or cut years off the loan. Cash-out refinances turn equity into capital for renovations, debt consolidation or another property. Either way, the first conversation is a break-even analysis: what it costs, what it saves, and how many months until you are ahead.",
      "Because Justin shops your refinance across many lenders, you are not stuck with whatever your current servicer is offering. Streamline options exist for FHA and VA borrowers that skip the appraisal entirely.",
    ],
    bullets: ["Rate-and-term and cash-out", "FHA and VA streamline refinances", "Break-even analysis before you apply", "No pressure to refinance if the numbers do not work"],
    home: true,
  },
  {
    slug: "jumbo",
    name: "Jumbo Loans",
    short: "Financing above the conforming limit with competitive pricing and flexible terms.",
    lede: "Larger loans need a lender who actually wants the file. Justin knows which ones do.",
    body: [
      "Loans above the conforming limit are where lender appetite varies the most. Some price jumbo aggressively and some barely offer it. Brokering the loan means Justin can place a jumbo file with the lender that is hungriest for it that month, which is how his clients regularly beat the rate quoted by their private bank.",
      "Jumbo programs can include interest-only options, asset depletion for retirees and business owners, and loan amounts well into the millions with as little as 10% down for qualified borrowers.",
    ],
    bullets: ["Loan amounts above the conforming limit", "As little as 10% down for qualified borrowers", "Interest-only and ARM options", "Asset depletion and complex income welcome"],
    home: true,
  },
  {
    slug: "first-time-buyers",
    name: "First-Time Buyers",
    short: "Low down payments, down payment assistance, and a guide through every step.",
    lede: "You do not need 20% down. You need someone who will explain the whole thing in plain English.",
    body: [
      "Most first-time buyers in Colorado can get into a home with 3% to 3.5% down, and many qualify for down payment assistance through state bond and grant programs. Justin has placed hundreds of first purchases and treats the education as part of the job: what the numbers mean, what to expect at each stage, and what not to do between pre-approval and closing.",
      "Credit not quite there yet? Justin offers pre-application credit coaching so you know exactly what to fix and how long it will take before you apply.",
    ],
    bullets: ["3% and 3.5% down programs", "Colorado down payment assistance", "Pre-application credit coaching", "Step-by-step guidance from offer to keys"],
    home: true,
  },
  {
    slug: "construction",
    name: "Construction Loans",
    short: "One-time-close and construction-to-permanent financing for new builds.",
    lede: "Building from the ground up takes a lender who understands draws, budgets and builders.",
    body: [
      "Construction financing is a specialty most loan officers avoid. Justin has structured construction and construction-to-permanent loans for custom builds and developer projects across the Front Range, including one-time-close programs that lock your permanent rate before the first shovel hits dirt.",
      "For developers and builders, Justin also helps cut through zoning, HOA, insurance and pre-sale requirements that can stall a project's end-buyer financing.",
    ],
    bullets: ["One-time-close construction-to-permanent", "Lot and land purchase financing", "Builder and developer partnerships", "Draw schedules that match how you build"],
    home: true,
  },
  {
    slug: "renovation",
    name: "Renovation Loans",
    short: "Buy and renovate with one loan, including FHA 203(k) and conventional options.",
    lede: "Finance the purchase and the remodel together, based on what the home will be worth when the work is done.",
    body: [
      "Renovation loans let you buy a home that needs work and fold the renovation budget into the mortgage. The appraisal is based on the after-improved value, so you are not limited by what the property is worth today. Conventional and FHA 203(k) options cover everything from a kitchen refresh to a full gut renovation.",
    ],
    bullets: ["FHA 203(k) and conventional renovation", "Based on after-improved value", "Purchase or refinance", "Contractor coordination and draw management"],
    home: true,
  },
  {
    slug: "investors",
    name: "Investment Property",
    short: "DSCR, bank statement and portfolio loans for investors and the self-employed.",
    lede: "Qualify on the property's cash flow, not your tax returns.",
    body: [
      "Investors and self-employed borrowers are often underserved by traditional lenders whose guidelines were written for W-2 employees. Justin places DSCR loans that qualify on rental income, bank statement loans that qualify on deposits, and portfolio products for situations that do not fit in a box.",
      "Whether it is your first rental or your fifteenth, the goal is the same: the right leverage on the right terms so the property performs.",
    ],
    bullets: ["DSCR loans qualified on rental income", "12 and 24 month bank statement programs", "Portfolio and non-QM options", "Financing for LLC-held properties"],
    home: true,
  },
  {
    slug: "va",
    name: "VA Loans",
    short: "Zero down, no mortgage insurance, and a broker who knows the VA process cold.",
    lede: "You earned the benefit. It should be easy to use.",
    body: [
      "VA loans offer 0% down, no monthly mortgage insurance and competitive rates for eligible veterans, active duty service members and surviving spouses. Justin handles certificates of eligibility, funding fee exemptions and VA appraisal requirements so the process feels like any other purchase.",
    ],
    bullets: ["0% down for eligible borrowers", "No monthly mortgage insurance", "VA IRRRL streamline refinances", "Certificate of eligibility handled for you"],
    home: true,
  },
  {
    slug: "heloc",
    name: "Home Equity",
    short: "HELOCs and second mortgages that leave your low first-mortgage rate untouched.",
    lede: "Tap your equity without giving up the rate you already have.",
    body: [
      "If your current mortgage rate is lower than what is available today, a cash-out refinance can cost you. A home equity line of credit or fixed-rate second mortgage lets you access equity for renovations, education or investment while keeping your first mortgage exactly as it is.",
    ],
    bullets: ["Keep your existing first-mortgage rate", "Lines of credit and fixed-rate seconds", "Fast closings", "Use funds for any purpose"],
    home: false,
  },
];

export default programs;
export const homePrograms = programs.filter((p) => p.home);
